import { useEffect, useState, useEffectEvent } from 'react'
import { useZusSessionHandler } from './ZusSessionProvider'
import { Loader, SectionCard } from '~/shared/ui'
import { Box, Paper } from '@mui/material'
import { ExpandedModeControls } from '~/features/member-profile'
import { logger } from '~/shared/lib/logger'
import { Props } from './types'

const ZUS_ZAP_URL = import.meta.env.VITE_ZUS_ZAP_URL

interface EmbedProps extends Props {
  /** Design-refresh shell: gutters + SectionCard-framed iframe with the
   * expand control in a header row instead of the sticky overlay. */
  refreshChrome?: boolean
}

interface ZapMessageEvent {
  data: {
    type: string
  }
}
const ZapIFrameConfigMessageType = 'ZusAggregatedProfileIFrameConfig'
const ZapIFrameReadyMessageType = 'ZusAggregatedProfileIFrameReady'

/**
 * An embedded view of Zus' Aggregated Profile
 * showing health information for the patient
 * Docs: https://docs.zushealth.com/docs/embeddable-ui-components-implementation-guide
 */
const ZusAggregatedProfileEmbed: React.FC<EmbedProps> = props => {
  const zusSession = useZusSessionHandler()
  const [iFrameRef, setIFrameRef] = useState<HTMLIFrameElement | null>(null)
  const [hostedZapReady, setHostedZapReady] = useState(false)

  useEffect(() => {
    // listener to know when iframe ZAP is ready
    const onMessageReady = ({ data }: ZapMessageEvent) => {
      if (data.type === ZapIFrameReadyMessageType) {
        logger.info('Received ZAP message: ZapIFrameReadyMessageType', data)
        setHostedZapReady(true)
      }
    }
    window.addEventListener('message', onMessageReady)
    return () => {
      window.removeEventListener('message', onMessageReady)
    }
  }, [])

  // An effect event: posted once the frame is ready, with the props of that moment.
  const loadConfig = useEffectEvent((frame: Window) => {
    const CTWProviderProps = {
      authToken: zusSession.accessToken,
      env: import.meta.env.VITE_ZUS_ENV,
      ehr: 'Firefly',
    }

    const PatientProviderProps = {
      patientID: String(props.patient.person.id),
      systemURL: import.meta.env.VITE_ZUS_PATIENT_SYSTEM,
    }
    const config = {
      type: ZapIFrameConfigMessageType,
      payload: {
        CTWProviderProps: CTWProviderProps,
        PatientProviderProps: PatientProviderProps,
        ZusAggregatedProfileProps: {
          patientConditionsAllProps: {
            onlyAllowAddOutsideConditions: true,
          },
        },
      },
    }
    frame.postMessage(config, ZUS_ZAP_URL)
  })

  useEffect(() => {
    if (hostedZapReady && iFrameRef?.contentWindow) {
      loadConfig(iFrameRef.contentWindow)
    }
  }, [hostedZapReady, iFrameRef?.contentWindow])

  const isLoading = !zusSession

  if (isLoading) return <Loader />

  const frame = (
    <iframe
      title="Embedded ZAP"
      id="zus-aggregated-profile"
      src={ZUS_ZAP_URL}
      ref={setIFrameRef}
      width="100%"
      height="100%"
      style={{ overflow: 'scroll', ...(props.refreshChrome ? { border: 0 } : {}) }}
    />
  )

  if (props.refreshChrome) {
    return (
      <Box sx={{ px: 3, pt: 1.5, pb: 3, height: '78vh', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <ExpandedModeControls />
        </Box>
        <SectionCard
          disablePadding
          sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
          bodySx={{ flex: 1, minHeight: 0, display: 'flex' }}
        >
          {frame}
        </SectionCard>
      </Box>
    )
  }

  return (
    <Box sx={{ paddingX: 1, height: '75vh', position: 'relative' }}>
      <Box
        component={Paper}
        sx={{ position: 'sticky', display: 'inline-block', top: 0, marginBottom: -0.5 }}
      >
        <ExpandedModeControls />
      </Box>
      {frame}
    </Box>
  )
}

export default ZusAggregatedProfileEmbed
